// ─── I corsi del maestro: l'elenco, e come se ne apre uno ────────────────────
//
// 🎓⭐⭐ **UN CORSO NON È UNA LEZIONE CHE SI RIPETE: è un gruppo di persone che
//    torna.** Ed è la ragione per cui ha una schermata sua invece di una spunta
//    «si ripete» dentro la prenotazione: quello che si guarda di un corso non è
//    un'ora, è **chi c'è dentro** — e quella è una cosa sola per tutte le date.
//
// 🗣️ Decisione sua fra le otto: *«corso: iscritti fissi — scritti una volta,
// valgono per tutte le date»*.
//
// ⚖️ **Due passi, e in quest'ordine: prima l'ORA, poi il nome e la fine.** Il
// giorno della settimana e la durata li porta con sé l'ora che si sceglie, e
// «fino a quando» si decide meglio sapendo da quando si parte. All'inverso si
// chiederebbe una data di fine prima di sapere quella di inizio.

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ETICHETTE_CADENZA } from '@pmo/shared';
import { chiama } from '../../lib/api.js';
import { Attesa, Guasto } from '../../components/pezzi.js';
import { CampoData } from '../../components/CampoData.js';
import { giornoPerEsteso, oggi, piuGiorni } from '../../lib/time.js';
import { SceltaDellOra } from './Prenota.js';
import { rinfrescaLaGiornata } from './pezzi.js';

interface CorsoInElenco {
  serieId: string;
  title: string | null;
  cadenza: string;
  dal: string;
  al: string;
  courtName: string;
  time: string;
  durationMinutes: number;
  quantiIscritti: number;
  volteRimaste: number;
}

export function Corsi(): React.JSX.Element {
  const corsi = useQuery({
    queryKey: ['maestro', 'corsi'],
    queryFn: () => chiama<{ items: CorsoInElenco[] }>('GET', '/maestro/corsi'),
  });

  if (corsi.isLoading) return <Attesa cosa="i tuoi corsi" />;
  if (corsi.error) return <Guasto errore={corsi.error} />;
  const items = corsi.data!.items;

  return (
    <>
      {/* 🔙 Dal 23/09 (voce 13) la barra in basso non c'è più: senza questa riga
          da qui non si tornerebbe da nessuna parte. */}
      <p>
        <Link className="indietro" to="/maestro">
          ‹ La mia agenda
        </Link>
      </p>
      <h2 className="titolo-maestro">I tuoi corsi</h2>

      {items.length === 0 ? (
        <section className="riquadro">
          <p className="tenue">
            Non hai ancora nessun corso. Un corso è un gruppo di persone che torna: si iscrivono una volta e
            valgono per tutte le date.
          </p>
        </section>
      ) : (
        <ul className="elenco-corsi">
          {items.map((c) => (
            <li key={c.serieId}>
              <Link to={`/maestro/corsi/${c.serieId}`}>
                <strong>{c.title ?? 'Corso'}</strong>
                <span className="tenue">
                  {ETICHETTE_CADENZA[c.cadenza as keyof typeof ETICHETTE_CADENZA] ?? c.cadenza} · {c.time} ·{' '}
                  {c.courtName}
                </span>
                <span>
                  {c.quantiIscritti === 1 ? '1 iscritto' : `${c.quantiIscritti} iscritti`}
                  {' · '}
                  {/* ⏳ «Quante ne restano» e non «quante sono»: a bordo campo la
                      domanda è se il corso è ancora vivo, non quanto è stato lungo. */}
                  {c.volteRimaste === 0
                    ? 'finito'
                    : c.volteRimaste === 1
                      ? 'ancora 1 volta'
                      : `ancora ${c.volteRimaste} volte`}
                </span>
                <span className="tenue piccolo">
                  dal {giornoPerEsteso(c.dal)} al {giornoPerEsteso(c.al)}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}

      <p className="gesti-lezione">
        <Link className="azione-principale" to="/maestro/corsi/nuovo">
          Apri un corso
        </Link>
      </p>
    </>
  );
}

/**
 * ⭐⭐ **APRIRE UN CORSO: prima l'ora, poi il nome e fin quando.**
 *
 * ⚠️ E il corso nasce **vuoto**: gli iscritti si mettono dalla sua scheda, dopo.
 * Non è pigrizia di schermata — iscrivere manda un messaggio a delle persone
 * vere, e mescolarlo col gesto che crea venti prenotazioni vorrebbe dire che un
 * errore di battitura sulla data finale fa partire venti avvisi prima che
 * qualcuno possa rileggere.
 */
export function ApriUnCorso(): React.JSX.Element {
  const vaiA = useNavigate();
  /* 🔙 Si torna **da dove si è venuti**: dal «+» dell'agenda (voce 13) si torna
     all'agenda, dall'elenco dei corsi all'elenco. Un «‹ I tuoi corsi» per chi
     non ci è mai passato porterebbe in un posto che non ha scelto. */
  const dallAgenda = (useLocation().state as { da?: 'agenda' } | null)?.da === 'agenda';
  const coda = useQueryClient();
  const [scelta, setScelta] = useState<{
    courtId: string;
    date: string;
    time: string;
    durationMinutes: number;
  } | null>(null);
  const [nome, setNome] = useState('');
  const [cadenza, setCadenza] = useState<'settimanale' | 'quindicinale'>('settimanale');
  /* ⏳ Tre mesi: è la lunghezza di un corso normale, e resta una proposta da
     cambiare — non una regola. Contarli a mano su un telefono è il genere di
     conto che si sbaglia. */
  const [fino, setFino] = useState(piuGiorni(oggi(), 90));

  const apri = useMutation({
    meta: { fatto: (d) => `corso aperto: ${(d as { quante: number }).quante} lezioni in calendario` },
    mutationFn: () =>
      chiama<{ serieId: string; quante: number; saltate: Array<{ date: string; perche: string }> }>(
        'POST',
        '/maestro/corsi',
        { ...scelta!, title: nome.trim(), cadenza, fino },
      ),
    onSuccess: (e) => {
      void coda.invalidateQueries({ queryKey: ['maestro', 'corsi'] });
      rinfrescaLaGiornata(coda);
      vaiA(`/maestro/corsi/${e.serieId}`);
    },
  });

  if (scelta === null) {
    return (
      <SceltaDellOra
        titolo="Quando si fa il corso"
        tornaA={dallAgenda ? '/maestro' : '/maestro/corsi'}
        tornaAChe={dallAgenda ? 'La mia agenda' : 'I tuoi corsi'}
        scegli={(s) => setScelta(s)}
        inCorso={false}
        errore={null}
      />
    );
  }

  return (
    <>
      <p>
        {/* 🔙 Si torna alla scelta dell'ora, non all'elenco: chi ha sbagliato ora
            deve poterla rifare senza ricominciare. */}
        <button type="button" className="indietro piatto" onClick={() => setScelta(null)}>
          ‹ Cambia l’ora
        </button>
      </p>
      <h2 className="titolo-maestro">Il corso</h2>

      <section className="riquadro">
        <p className="tenue">
          {giornoPerEsteso(scelta.date)} alle {scelta.time} · {scelta.durationMinutes} minuti
        </p>

        <label className="campo">
          <span>Come si chiama</span>
          <input
            value={nome}
            onChange={(e) => setNome(e.target.value)}
            placeholder="Corso adulti principianti"
            maxLength={120}
            autoComplete="off"
          />
        </label>

        {/* ⚖️ Due cadenze e non le otto del calendario: un corso si fa a
            settimane, e otto voci su un telefono sono otto modi di toccare
            quella sbagliata (⇒ `aproUnCorsoSchema`, dove sta il perché).
            📱 Voce 15, 23/09/2026: i due bottoni stanno UNO SOTTO L'ALTRO. In
            riga chiedevano 349 px, e a 320 e 360 la pagina scorreva di lato
            (larga 386); a 390 «Ogni due settimane» usciva dal riquadro. Sopra
            hanno il loro nome, come gli altri due campi. */}
        <div className="campo ogni-quanto">
          <span>Ogni quanto</span>
          <div className="durate-maestro cadenza" role="group" aria-label="Ogni quanto">
            <button
              type="button"
              className={cadenza === 'settimanale' ? 'scelto' : ''}
              aria-pressed={cadenza === 'settimanale'}
              onClick={() => setCadenza('settimanale')}
            >
              Ogni settimana
            </button>
            <button
              type="button"
              className={cadenza === 'quindicinale' ? 'scelto' : ''}
              aria-pressed={cadenza === 'quindicinale'}
              onClick={() => setCadenza('quindicinale')}
            >
              Ogni due settimane
            </button>
          </div>
        </div>

        {/* 🗓️ La data la scriviamo noi, in italiano: 🗣️ *«Sì, la data scrivila
            tu in italiano»* (17/09/2026). Con `type="date"` il telefono la
            disegnava nella sua lingua, e qui compariva 12/23/2026. */}
        <CampoData etichetta="Fino a quando" value={fino} min={scelta.date} onChange={setFino} />

        <p className="gesti-lezione">
          <button
            type="button"
            disabled={apri.isPending || nome.trim().length < 2 || fino === ''}
            onClick={() => apri.mutate()}
          >
            {apri.isPending ? 'Apro il corso…' : 'Apri il corso'}
          </button>
        </p>
        {/* ⚠️ Il rifiuto si legge qui sotto, dove si è appena premuto. */}
        <Guasto errore={apri.error} />
      </section>
    </>
  );
}
